<script setup lang="ts">
    import { getConfig, klinikenApi } from "@/api";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { Trigger } from "@/components/forms/Form/types";
    import { Option } from "@/components/forms/inputs/Autocomplete/types";
    import { useStore } from "@/store";
    import { convertToSubsetName } from "@/store/modules/helpers/baseSubset";
    import { SparrValue } from "@/store/modules/recept/helpers";
    import ReceptforskrivningFormPortal from "@/tabs/Receptforskrivning/Forskrivning/Form/ReceptforskrivningFormPortal.vue";
    import BehandlingSection from "@/tabs/Receptforskrivning/Forskrivning/Form/sections/BehandlingSection.vue";
    import DoseringSection from "@/tabs/Receptforskrivning/Forskrivning/Form/sections/DoseringSection.vue";
    import ExpedieringsdetaljerSection from "@/tabs/Receptforskrivning/Forskrivning/Form/sections/ExpedieringsdetaljerSection.vue";
    import LakemedelSection from "@/tabs/Receptforskrivning/Forskrivning/Form/sections/LakemedelSection.vue";
    import SparrSection from "@/tabs/Receptforskrivning/Forskrivning/Form/sections/SparrSection.vue";
    import {
        ANNAT_VAL,
        capitalize,
        filterNLLIssuesHumanReadable,
        filterNLLIssuesSevere,
        parseMedicationRequestErrors,
        SilProductType,
    } from "@/tabs/Receptforskrivning/helpers";
    import ReceptforskrivningMallAutocomplete from "@/tabs/Receptforskrivning/ReceptforskrivningMallAutocomplete.vue";
    import { openDialog } from "@/utils";
    import { AxiosError } from "axios";
    import moment from "moment/moment";
    import { v4 as uuid4 } from "uuid";
    import { provide, ref, toRefs } from "vue";

    const store = useStore();

    // Duplicerad i module, pga att Vue inte klarar av att hantera enum imports på ett vettigt sätt
    enum ValidationSeverity {
        Low = "low",
        Warning = "warning",
        Moderate = "moderate",
        Error = "error",
    }

    const props = withDefaults(
        defineProps<{
            /**
             * Id på tabben som renderar komponenten
             */
            tabId: number;
            /**
             * Id på receptförskrivningen, vid redigering
             */
            receptforskrivningId?: string;
            editingSamling?: boolean;
            // Är det en förnyelse av en förskrivning?
            fornya?: boolean;
        }>(),
        {
            fornya: false,
            editingSamling: false,
        }
    );

    const emit = defineEmits<{
        (event: "cancel", payload: { hasUnsavedData?: boolean }): void;
        (event: "submit", payload: { id?: string; step: "form" | "display" | "list" }): void;
        (event: "action", payload: { step: "form" | "display" | "list"; id?: string }): void;
    }>();

    const { tabId, receptforskrivningId, fornya } = toRefs(props);

    const formRef = ref();
    const expedieringsdetaljerSection = ref();
    const lakemedelSection = ref();
    const behandlingSection = ref();
    const doseringSection = ref();
    const sparrSection = ref();
    /**
     * Har användaren valt läkemedel? Sätts till false om användaren väljer att gå in och byta läkemedel.
     * Används för att avgöra vilka sektioner som ska vara expanderade
     */
    const hasSelectedLakemedel = ref(!!receptforskrivningId?.value);
    const existingSparr = ref<SparrValue | null>(null);

    provide("formRef", formRef);

    defineExpose({
        hasUnsavedData: () => formRef?.value?.hasUnsavedData(),
    });

    /**
     * Callback vid emit accept från footern
     */
    const onAccept = async () => {
        const formData = await formRef?.value?.manualSubmit();

        if (!formData) {
            return;
        }

        const id = receptforskrivningId?.value ? receptforskrivningId.value : uuid4();

        try {
            const {
                lakemedelId,
                nplId,
                // START Ska inte skickas till BE. Duplicerat i ReceptforskrivningDisplayPortal
                id: forskrivningId,
                sokLakemedel,
                silProductType,
                forhandsvisning,
                visa_administrering,
                administrering_all,
                mall,
                mallAutocomplete,
                // END Ska inte skickas till BE
                // apotek är hela optionen och ska inte skickas till BE as is
                apotek,
                ...payload
            } = formData;

            const { data } = await klinikenApi.post<
                Array<{
                    code: string;
                    display: string;
                    severity: ValidationSeverity;
                }>
            >(
                fornya.value ? "nll/forskrivningar/fornya/verify/" : "nll/forskrivningar/create/verify/",
                {
                    ...payload,
                    apotek: (apotek as Option)?.value,
                    patient: store.getters["patientData/currentId"],
                    lakemedel_npl_id: nplId,
                    lakemedel_npl_pack_id: lakemedelId,
                },
                getConfig({
                    headers: store.getters["nll/headers"],
                })
            );

            const severeIssues = filterNLLIssuesSevere(data);

            if (severeIssues?.length) {
                throw new Error(
                    filterNLLIssuesHumanReadable(severeIssues)
                        ?.map((message) => message.display)
                        .join("\n")
                );
            }

            // Sparar alla läsbara meddelanden oavsett severity
            store.commit("receptforskrivning/addReceptforskrivning", {
                receptforskrivning: {
                    ...formData,
                    validationMessages: filterNLLIssuesHumanReadable(data),
                    id,
                },
            });

            emit("submit", { id, step: "display" });
        } catch (error) {
            openDialog({
                description: (error as AxiosError)?.response?.status
                    ? "Förskrivningen kunde inte kontrolleras."
                    : "Förskrivningen innehåller fel.",
                lines: (error as AxiosError)?.response?.status
                    ? parseMedicationRequestErrors({ id, data: (error as AxiosError)?.response?.data })
                    : (error as Error).message.split("\n"),
                type: "error",
            });
        }
    };

    /**
     * Callback vid emit cancel från footern
     */
    const onCancel = async () => {
        emit("cancel", { hasUnsavedData: formRef?.value?.hasUnsavedData() });
    };

    const onLakemedelSelected = ({ value }: { value: boolean }) => {
        hasSelectedLakemedel.value = value;
    };

    const onAction = ({ step, id }: { step: "form" | "display" | "list"; id?: string }) => {
        emit("action", { step, id });
    };

    const getLakemedelSubsetName = () => {
        return convertToSubsetName({
            params: {
                nplPackId: formRef?.value?.getFieldValue({ name: "lakemedelId" }),
                nplId: formRef?.value?.getFieldValue({ name: "nplId" }),
            },
        });
    };

    const addPunctuation = ({ text }: { text: string | null }) => {
        if (!text) {
            return null;
        }

        return `${text}${text.endsWith(".") ? "" : "."}`;
    };

    const formTriggers: Trigger[] = [
        {
            fieldName: "tidsenhet",
            dependentOn: "tid_mellan_uttag",
            action: ({ value, dependentOnValue }) => {
                // tidsenhet ska inte påverkas vid förnya
                if (fornya.value) {
                    return;
                }

                if (dependentOnValue) {
                    return {
                        value,
                        disabled: false,
                    };
                }

                return {
                    value: null,
                    disabled: true,
                };
            },
        },
        {
            fieldName: [
                "administreringsvag",
                "administreringsmetod",
                "administreringsstalle",
                "precisering_administreringsstalle",
                "medicinteknisk_produkt",
            ],
            dependentOn: "visa_administrering",
            action: ({ value, dependentOnValue }) => {
                const dependentOnValueTyped = dependentOnValue as string[];
                const valueTyped = value as { [key: string]: string };

                if (!dependentOnValue) {
                    return [];
                }

                // Fälten ska alltid vara disabled vid förnya
                return [
                    {
                        fieldName: "administreringsvag",
                        disabled: fornya.value ? true : !dependentOnValueTyped.includes("vag"),
                        required: fornya.value ? false : dependentOnValueTyped.includes("vag"),
                        value: dependentOnValueTyped.includes("vag") ? valueTyped.administreringsvag : null,
                    },
                    {
                        fieldName: "administreringsmetod",
                        disabled: fornya.value ? true : !dependentOnValueTyped.includes("metod"),
                        required: fornya.value ? false : dependentOnValueTyped.includes("metod"),
                        value: dependentOnValueTyped.includes("metod") ? valueTyped.administreringsmetod : null,
                    },
                    {
                        fieldName: "administreringsstalle",
                        disabled: fornya.value ? true : !dependentOnValueTyped.includes("stalle"),
                        required: fornya.value ? false : dependentOnValueTyped.includes("stalle"),
                        value: dependentOnValueTyped.includes("stalle") ? valueTyped.administreringsstalle : null,
                    },
                    {
                        fieldName: "precisering_administreringsstalle",
                        disabled: fornya.value ? true : !dependentOnValueTyped.includes("stalle"),
                        required: false,
                        value: dependentOnValueTyped.includes("stalle")
                            ? valueTyped.precisering_administreringsstalle
                            : null,
                    },
                    {
                        fieldName: "medicinteknisk_produkt",
                        disabled: fornya.value ? true : !dependentOnValueTyped.includes("medicinteknisk_produkt"),
                        required: fornya.value ? false : dependentOnValueTyped.includes("medicinteknisk_produkt"),
                        value: dependentOnValueTyped.includes("medicinteknisk_produkt")
                            ? valueTyped.medicinteknisk_produkt
                            : null,
                    },
                ];
            },
        },
        {
            fieldName: ["forhandsvisning", "administreringsinstruktion"],
            dependentOn: [
                "behandlingsandamal",
                "dosering",
                // START Administration
                "administreringsmetod",
                "administreringsmetod_annan",
                "administreringsvag",
                "administreringsvag_annan",
                "administreringsstalle",
                "administreringsstalle_annan",
                "precisering_administreringsstalle",
                "precisering_administreringsstalle_annan",
                "medicinteknisk_produkt",
                "medicinteknisk_produkt_annan",
                // SLUT Administration
                "annan_instruktion",
            ],
            action: ({ dependentOnValue }) => {
                const dependentOnValueTyped = dependentOnValue as { [key: string]: string };

                const administreringsmetod = store.getters["nll/administreringsmetoderById"]({
                    id: dependentOnValueTyped?.administreringsmetod,
                });
                const administreringsvag = store.getters["nll/administreringsvagarById"]({
                    id: dependentOnValueTyped?.administreringsvag,
                });
                const administreringsstalle = store.getters["nll/administreringsstallenById"]({
                    id: dependentOnValueTyped?.administreringsstalle,
                });
                const precisering_administreringsstalle = store.getters["nll/preciseringarById"]({
                    id: dependentOnValueTyped?.precisering_administreringsstalle,
                });
                const medicinteknisk_produkt = store.getters["nll/medicintekniskaProdukterById"]({
                    id: dependentOnValueTyped?.medicinteknisk_produkt,
                });

                const content = [];
                // Det är i den här ordningen de ska skrivas ut. Don't move around inom arrayen
                const administreringContent = [
                    administreringsmetod?.patient_display ?? administreringsmetod?.label,
                    dependentOnValueTyped?.administreringsmetod_annan,
                    administreringsvag?.patient_display ?? administreringsvag?.label,
                    dependentOnValueTyped?.administreringsvag_annan,
                    administreringsstalle?.patient_display ?? administreringsstalle?.label,
                    dependentOnValueTyped?.administreringsstalle_annan,
                    precisering_administreringsstalle?.patient_display ?? precisering_administreringsstalle?.label,
                    dependentOnValueTyped?.precisering_administreringsstalle_annan,
                    medicinteknisk_produkt?.patient_display ?? medicinteknisk_produkt?.label,
                    dependentOnValueTyped?.medicinteknisk_produkt_annan,
                ].filter((text) => text);
                const capitalizedAdministreringContent = administreringContent
                    .map((text) => addPunctuation({ text: capitalize({ text }) }))
                    .join(" ");

                // Det är i den här ordningen de ska skrivas ut. Don't move around pushes
                content.push(
                    capitalize({
                        text: dependentOnValueTyped?.behandlingsandamal,
                    })
                );
                content.push(capitalize({ text: dependentOnValueTyped?.dosering }));
                content.push(capitalizedAdministreringContent);
                content.push(capitalize({ text: dependentOnValueTyped?.annan_instruktion }));

                const contentString = content
                    .filter((text) => text)
                    .map((text) => addPunctuation({ text }))
                    .join(" ");

                return [
                    {
                        fieldName: "forhandsvisning",
                        value: contentString.trim(),
                    },
                    {
                        fieldName: "administreringsinstruktion",
                        value: capitalizedAdministreringContent.trim(),
                    },
                ];
            },
        },
        {
            fieldName: "annan_orsak",
            dependentOn: "behandlingsorsak",
            action: ({ value, dependentOnValue }) => {
                // annan_orsak ska inte påverkas vid förnya
                if (fornya.value) {
                    return;
                }

                if ((dependentOnValue as string) === ANNAT_VAL) {
                    return {
                        value,
                        disabled: false,
                        required: !fornya.value,
                    };
                }

                return {
                    value: null,
                    disabled: true,
                    required: false,
                };
            },
        },
        {
            fieldName: "behandlingsandamal",
            dependentOn: "behandlingsorsak",
            action: ({ value, dependentOnValue, initial }) => {
                if (dependentOnValue && !initial) {
                    const behandlingsorsak = store.getters["nll/behandlingsorsakerById"]({
                        id: dependentOnValue as string,
                    });

                    if (behandlingsorsak?.patient_display) {
                        const trimmedDisplay = `${value ? `${value} ` : ""}${behandlingsorsak?.patient_display}`.trim();

                        if (trimmedDisplay.endsWith(".")) {
                            return { value: trimmedDisplay.slice(0, -1) };
                        }

                        return { value: trimmedDisplay };
                    }
                }

                return {
                    value,
                };
            },
        },
        {
            fieldName: "senaste_datum_for_uppfoljning_av_behandling",
            dependentOn: "senaste_datum_for_avslut_av_behandling",
            action: ({ value, dependentOnValue }) => {
                return {
                    value,
                    disabled: !!dependentOnValue,
                };
            },
        },
        {
            fieldName: "senaste_datum_for_avslut_av_behandling",
            dependentOn: "senaste_datum_for_uppfoljning_av_behandling",
            action: ({ value, dependentOnValue }) => {
                return {
                    value,
                    disabled: !!dependentOnValue,
                };
            },
        },
        {
            fieldName: [
                "administreringsmetod",
                "behandlingsorsak",
                "administreringsvag",
                "administreringsstalle",
                "precisering_administreringsstalle",
                "medicinteknisk_produkt",
            ],
            dependentOn: ["lakemedelId", "administrering_all"],
            action: async ({ value, dependentOnValue }) => {
                const valueTyped = value as { [key: string]: string };
                const dependentOnTyped = dependentOnValue as { administrering_all?: boolean; lakemedelId: string };

                /**
                 * lakemedelId sätts manuellt vid val av läkemedel, efter hämtning av data som ska vara med i options.
                 * Se LakemedelSection.vue
                 */

                if (!dependentOnTyped?.lakemedelId) {
                    return;
                }

                /**
                 * Gör en hämtning av kodverk oberoende av läkemdel om administrering_all är true och
                 * om metoder är tomt. Alla olika typer av kodverk hämtas samtidigt. Om metoder är tom är kodverk
                 * oberoende av läkemedel inte hämtade än
                 */

                if (
                    dependentOnTyped.administrering_all &&
                    !store.getters["nll/administreringsmetoderSubset"]({
                        subsetName: "default",
                    }).length
                ) {
                    await store.dispatch("nll/fetchKodverkByLakemedel");
                }

                const behandlingsorsakOptions = store.getters["nll/behandlingsorsakerSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const administreringsmetodOptions = store.getters["nll/administreringsmetoderSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const administreringsvagOptions = store.getters["nll/administreringsvagarSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const administreringsstalleOptions = store.getters["nll/administreringsstallenSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const preciseringOptions = store.getters["nll/preciseringarSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });
                const medicintekniskProduktOptions = store.getters["nll/medicintekniskaProdukterSubset"]({
                    subsetName: dependentOnTyped.administrering_all ? "default" : getLakemedelSubsetName(),
                });

                // value: Om administrering_all kryssas ur och värdet inte finns kvar som alternativ blir value null.
                return [
                    {
                        fieldName: "behandlingsorsak",
                        value: behandlingsorsakOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.behandlingsorsak)
                            ? valueTyped.behandlingsorsak
                            : null,
                        options: behandlingsorsakOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "administreringsmetod",
                        value: administreringsmetodOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.administreringsmetod)
                            ? valueTyped.administreringsmetod
                            : null,
                        options: administreringsmetodOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "administreringsvag",
                        value: administreringsvagOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.administreringsvag)
                            ? valueTyped.administreringsvag
                            : null,
                        options: administreringsvagOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "administreringsstalle",
                        value: administreringsstalleOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.administreringsstalle)
                            ? valueTyped.administreringsstalle
                            : null,
                        options: administreringsstalleOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "precisering_administreringsstalle",
                        value: preciseringOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.precisering_administreringsstalle)
                            ? valueTyped.precisering_administreringsstalle
                            : null,
                        options: preciseringOptions,
                        setOptionsDefault: true,
                    },
                    {
                        fieldName: "medicinteknisk_produkt",
                        value: medicintekniskProduktOptions
                            .map((item: { value: string }) => item.value)
                            .includes(valueTyped.medicinteknisk_produkt)
                            ? valueTyped.medicinteknisk_produkt
                            : null,
                        options: medicintekniskProduktOptions,
                        setOptionsDefault: true,
                    },
                ];
            },
        },
        {
            fieldName: [
                "senaste_datum_for_uppfoljning_av_behandling",
                "senaste_datum_for_avslut_av_behandling",
                "sista_doseringsdag",
            ],
            dependentOn: "extraData",
            action: ({ dependentOnValue }) => {
                const dependentOnValueTyped = dependentOnValue as
                    | {
                          senaste_datum_for_uppfoljning_av_behandling: string | null;
                          senaste_datum_for_avslut_av_behandling: string | null;
                          sista_doseringsdag: string | null;
                      }
                    | undefined;
                const returnData = [];
                const noteStart = "Notera att datumet är satt på förskrivningen som förnyas:";
                const baseData = {
                    suffixType: "note" as "note",
                };

                if (dependentOnValueTyped?.senaste_datum_for_uppfoljning_av_behandling) {
                    returnData.push({
                        ...baseData,
                        fieldName: "senaste_datum_for_uppfoljning_av_behandling",
                        suffix: `${noteStart} ${dependentOnValueTyped.senaste_datum_for_uppfoljning_av_behandling}`,
                    });
                }

                if (dependentOnValueTyped?.senaste_datum_for_avslut_av_behandling) {
                    returnData.push({
                        ...baseData,
                        fieldName: "senaste_datum_for_avslut_av_behandling",
                        suffix: `${noteStart} ${dependentOnValueTyped.senaste_datum_for_avslut_av_behandling}`,
                    });
                }

                if (dependentOnValueTyped?.sista_doseringsdag) {
                    returnData.push({
                        ...baseData,
                        fieldName: "sista_doseringsdag",
                        suffix: `${noteStart} ${dependentOnValueTyped.sista_doseringsdag}`,
                    });
                }

                return returnData;
            },
        },
    ];

    const fetchFormData = async () => {
        if (receptforskrivningId?.value) {
            const receptforskrivning = store.getters["receptforskrivning/receptforskrivningById"]({
                id: receptforskrivningId.value,
            });

            if (receptforskrivning.sparr_legal_ground && receptforskrivning.sparr_subject_type) {
                existingSparr.value = {
                    sparr_legal_ground: receptforskrivning.sparr_legal_ground,
                    sparr_subject_type: receptforskrivning.sparr_subject_type,
                };
            }

            return {
                ...receptforskrivning,
                sista_giltighetsdag:
                    receptforskrivning.sista_giltighetsdag ?? moment().add(1, "year").format("yyyy-MM-DD HH:mm"),
            };
        }

        const patient = store.getters["patientData/patient"];

        /**
         * Default sätts här och i LakemedelSection vid ändring av valt läkemedel
         */
        return {
            formansberattigad_patient: !!patient.careCoverage,
            visa_administrering: ["metod"],
            administreringsmetod: null,
            silProductType: SilProductType.Approved,
            substitution_tillaten: true,
            sista_giltighetsdag: moment().add(1, "year").format("yyyy-MM-DD HH:mm"),
        };
    };
</script>

<template>
    <KForm
        :id="`receptforskrivning-form-${tabId}`"
        class="receptforskrivning-form"
        ref="formRef"
        :triggers="formTriggers"
        :initial-values-getter="fetchFormData"
        :listeners="[
            ...(expedieringsdetaljerSection?.formListeners ?? []),
            ...(lakemedelSection?.formListeners ?? []),
            ...(doseringSection?.formListeners ?? []),
            ...(behandlingSection?.formListeners ?? []),
            ...(sparrSection?.formListeners ?? []),
        ]"
    >
        <template #default>
            <div v-if="!fornya" class="form-content">
                <ReceptforskrivningMallAutocomplete :tabId="tabId" />
            </div>
            <LakemedelSection
                ref="lakemedelSection"
                :receptforskrivningsId="receptforskrivningId"
                @lakemedelSelected="onLakemedelSelected"
                :hasSelectedLakemedel="hasSelectedLakemedel"
                :fornya="fornya"
                :tabId="tabId"
            />
            <ExpedieringsdetaljerSection
                ref="expedieringsdetaljerSection"
                :receptforskrivningId="receptforskrivningId"
                :hasSelectedLakemedel="hasSelectedLakemedel"
                :fornya="fornya"
            />
            <BehandlingSection ref="behandlingSection" :hasSelectedLakemedel="hasSelectedLakemedel" :fornya="fornya" />
            <DoseringSection
                ref="doseringSection"
                :receptforskrivningId="receptforskrivningId"
                :hasSelectedLakemedel="hasSelectedLakemedel"
                :fornya="fornya"
            />
            <SparrSection
                ref="sparrSection"
                :hasSelectedLakemedel="hasSelectedLakemedel"
                :existingSparr="existingSparr"
                :receptforskrivningId="receptforskrivningId"
                :fornya="fornya"
            />
            <!-- Används för att justera hur formuläret ska renderas vid förnya -->
            <KText ignoreValue hidden name="extraData" />
            <!-- Targettar och renderar i portalen i Receptforskrivning.vue -->
            <ReceptforskrivningFormPortal
                :tabId="tabId"
                formIdPrefix="receptforskrivning-form-"
                @accept="onAccept"
                @cancel="onCancel"
                @action="onAction"
                :editingSamling="editingSamling"
                :formRef="formRef"
                :fornya="fornya"
            />
        </template>
    </KForm>
</template>

<style scoped lang="scss">
    .receptforskrivning-form {
        // Behövs för att portal (footern) i tabben ska hamna längst ner
        flex-grow: 1;
    }
</style>
